// src/pages/Wallet.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { useAuth } from '../hooks/useAuth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import logo from '../assets/logo.png';
import { getDatabase, ref, get } from 'firebase/database';
import { 
  ArrowLeft, Wallet as WalletIcon, Plus, ArrowUpRight, Clock, Copy, Check, 
  WifiOff, History, Shield, TrendingUp, Award, Zap, 
  Gift, Send, AlertCircle, Diamond, Crown,
  Loader2, RefreshCw, XCircle, Timer, Info, Sparkles, CheckCircle
} from 'lucide-react';

interface Transaction {
  id: string;
  type: 'credit' | 'debit';
  amount: number;
  reason: string;
  time: number;
  status?: 'pending' | 'success' | 'rejected';
  rejectReason?: string;
}

// Add this new interface for payment state
interface PaymentState {
  id: string;
  amount: number;
  status: 'idle' | 'pending' | 'success' | 'failed' | 'expired' | 'cancelled';
  createdAt: number;
  timeRemaining: number;
}

const Wallet = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [balance, setBalance] = useState(0);
  const [userName, setUserName] = useState('');
  const [loading, setLoading] = useState(true);
  const [offline, setOffline] = useState(false);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [copied, setCopied] = useState(false);
  const [showAddMoneyModal, setShowAddMoneyModal] = useState(false);
  const [addAmount, setAddAmount] = useState('');
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawUpi, setWithdrawUpi] = useState('');
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [selectedQuickAmount, setSelectedQuickAmount] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'history'>('overview');
  
  // Payment states
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentId, setPaymentId] = useState("");
  const [paymentStatus, setPaymentStatus] = useState<'idle' | 'pending' | 'success' | 'failed' | 'expired' | 'cancelled'>('idle');
  const [paymentAmount, setPaymentAmount] = useState<number | null>(null);
  const [paymentCreatedAt, setPaymentCreatedAt] = useState<number | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [isPolling, setIsPolling] = useState(false);

  const [paymentWindowRef, setPaymentWindowRef] = useState<Window | null>(null);

// ============================================
// CHECK FOR PAYMENT ON LOAD
// ============================================
useEffect(() => {
  const urlPaymentId = searchParams.get('paymentId');

  const savedPaymentId = localStorage.getItem('paymentId');
  const savedPaymentAmount = localStorage.getItem('paymentAmount');
  const savedPaymentCreatedAt = localStorage.getItem('paymentCreatedAt');

  // Prefer paymentId returned by TranzUPI
  const finalPaymentId = urlPaymentId || savedPaymentId;

  if (!finalPaymentId) {
    return;
  }

  setPaymentId(finalPaymentId);
  setPaymentStatus('pending');

  if (savedPaymentAmount) {
    setPaymentAmount(parseInt(savedPaymentAmount));
  }

  if (savedPaymentCreatedAt) {
    setPaymentCreatedAt(parseInt(savedPaymentCreatedAt));
  }
}, [searchParams]);

  // ============================================
  // PAYMENT TIMER - Show remaining time
  // ============================================
  useEffect(() => {
    if (paymentStatus !== "pending" || !paymentCreatedAt) {
      setTimeRemaining(0);
      return;
    }

    const updateTimer = () => {
      const elapsed = Math.floor((Date.now() - paymentCreatedAt) / 1000);
      const remaining = Math.max(0, 300 - elapsed); // 5 minutes = 300 seconds
      setTimeRemaining(remaining);
      
      // If time is up, mark as expired
      if (remaining === 0 && paymentStatus === "pending") {
        setPaymentStatus("expired");
        setPaymentLoading(false);
        localStorage.removeItem("paymentId");
        localStorage.removeItem("paymentAmount");
        localStorage.removeItem("paymentCreatedAt");
        setPaymentId("");
        setPaymentAmount(null);
        setPaymentCreatedAt(null);
        setIsPolling(false);

  if (paymentWindowRef) {
    try {
      paymentWindowRef.close();
    } catch {}
    setPaymentWindowRef(null);
  }
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [paymentStatus, paymentCreatedAt]);

  // ============================================
  // FORMAT TIME REMAINING
  // ============================================
  const formatTimeRemaining = (seconds: number) => {
    if (seconds <= 0) return "00:00";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  // ============================================
  // REFRESH WALLET DATA (Single API Call)
  // ============================================
  const refreshWallet = async () => {
    const user = auth.currentUser;
    if (!user) return null;

    try {
      const functions = getFunctions();
      const refreshWalletFn = httpsCallable(functions, 'refreshWallet');
      const result = await refreshWalletFn({});
      const data = result.data as {
        balance: number;
        userName: string;
        name: string;
        transactions: Transaction[];
      };

      setBalance(data.balance || 0);
      setUserName(data.userName || '');
      setTransactions(data.transactions || []);

      localStorage.setItem(`wallet_balance_${user.uid}`, String(data.balance || 0));
      localStorage.setItem(`wallet_transactions_${user.uid}`, JSON.stringify(data.transactions || []));

      return data;
    } catch (error) {
      console.error('Error refreshing wallet:', error);
      throw error;
    }
  };

  // ============================================
  // CHECK PAYMENT STATUS
  // ============================================
  const checkPaymentStatus = async () => {
    if (!paymentId) return;

    try {
      const functions = getFunctions();
      const checkStatus = httpsCallable(functions, "checkPaymentStatus");

      const result = await checkStatus({
        paymentId: paymentId
      });

      const data = result.data as {
        status: string;
        message?: string;
        amount?: number;
      };


      switch (data.status) {
        case "SUCCESS":
          setPaymentStatus("success");
          setPaymentLoading(false);
          setIsPolling(false);
          
          if (paymentWindowRef) {
          try {
            paymentWindowRef.close();
          } catch {}
          setPaymentWindowRef(null);
        }

          const walletData = await refreshWallet();
          const addedAmount = data.amount || paymentAmount || 0;
          
          if (walletData) {
            alert(`✅ Payment Successful! ₹${addedAmount} added to your wallet.`);
          }
          
          localStorage.removeItem("paymentId");
          localStorage.removeItem("paymentAmount");
          localStorage.removeItem("paymentCreatedAt");
          setPaymentId("");
          setPaymentAmount(null);
          setPaymentCreatedAt(null);
          setShowAddMoneyModal(false);
          setAddAmount("");
          setSelectedQuickAmount(null);
          setTimeRemaining(0);
          
          setTimeout(() => {
            setPaymentStatus("idle");
          }, 3000);
          break;

        case "FAILED":
          setPaymentStatus("failed");
          setPaymentLoading(false);
          setIsPolling(false);

          // 🔥 Add this
  if (paymentWindowRef) {
    try {
      paymentWindowRef.close();
    } catch {}
    setPaymentWindowRef(null);
  }

          alert("❌ Payment Failed. Please try again.");
          localStorage.removeItem("paymentId");
          localStorage.removeItem("paymentAmount");
          localStorage.removeItem("paymentCreatedAt");
          setPaymentId("");
          setPaymentAmount(null);
          setPaymentCreatedAt(null);
          setShowAddMoneyModal(false);
          setAddAmount("");
          setTimeRemaining(0);
          setTimeout(() => setPaymentStatus("idle"), 3000);
          break;

        case "EXPIRED":
          setPaymentStatus("expired");
          setPaymentLoading(false);
          setIsPolling(false);

          // 🔥 Add this
  if (paymentWindowRef) {
    try {
      paymentWindowRef.close();
    } catch {}
    setPaymentWindowRef(null);
  }

          alert("⏰ Payment Expired. Please try again.");
          localStorage.removeItem("paymentId");
          localStorage.removeItem("paymentAmount");
          localStorage.removeItem("paymentCreatedAt");
          setPaymentId("");
          setPaymentAmount(null);
          setPaymentCreatedAt(null);
          setShowAddMoneyModal(false);
          setAddAmount("");
          setTimeRemaining(0);
          setTimeout(() => setPaymentStatus("idle"), 3000);
          break;

        case "CANCELLED":
          setPaymentStatus("cancelled");
          setPaymentLoading(false);
          setIsPolling(false);

          // 🔥 Add this
  if (paymentWindowRef) {
    try {
      paymentWindowRef.close();
    } catch {}
    setPaymentWindowRef(null);
  }

          alert("❌ Payment Cancelled.");
          localStorage.removeItem("paymentId");
          localStorage.removeItem("paymentAmount");
          localStorage.removeItem("paymentCreatedAt");
          setPaymentId("");
          setPaymentAmount(null);
          setPaymentCreatedAt(null);
          setShowAddMoneyModal(false);
          setAddAmount("");
          setTimeRemaining(0);
          setTimeout(() => setPaymentStatus("idle"), 3000);
          break;

        default:
          break;
      }
    } catch (err) {
      console.error('Error checking payment status:', err);
    }
  };

  // ============================================
  // PAYMENT POLLING
  // ============================================
  useEffect(() => {
    if (paymentStatus !== "pending") {
      setIsPolling(false);
      return;
    }

    setIsPolling(true);
    
    checkPaymentStatus();
    
    const interval = setInterval(() => {
      if (paymentStatus === "pending") {
        checkPaymentStatus();
      }
    }, 5000);

    return () => {
      clearInterval(interval);
      setIsPolling(false);
    };
  }, [paymentStatus, paymentId]);

  // ============================================
  // CANCEL PAYMENT
  // ============================================
const cancelPayment = () => {
  if (window.confirm('Are you sure you want to cancel this payment?')) {
    // 🔥 Add this line:
    if (paymentWindowRef) {
      try {
        paymentWindowRef.close();
      } catch {}
      setPaymentWindowRef(null);
    }
    
    setPaymentStatus("cancelled");
    // ... rest of your existing code
  }
};

// ============================================
// LOAD USER DATA
// Authentication is handled by AuthProvider
// ============================================
useEffect(() => {
  if (!user) {
    return;
  }

  const loadUserData = async () => {
    try {
      setLoading(true);

      if (!navigator.onLine) {
        setOffline(true);

        const cachedBalance = localStorage.getItem(
          `wallet_balance_${user.uid}`
        );

        const cachedTransactions = localStorage.getItem(
          `wallet_transactions_${user.uid}`
        );

        if (cachedBalance) {
          setBalance(parseInt(cachedBalance));
        }

        if (cachedTransactions) {
          try {
            setTransactions(JSON.parse(cachedTransactions));
          } catch (error) {
            setTransactions([]);
          }
        }

        setLoading(false);
        return;
      }

      await refreshWallet();

      setLoading(false);

    } catch (error) {
      console.error('❌ Error loading wallet:', error);
      setOffline(true);
      setLoading(false);
    }
  };

  loadUserData();
}, [user]);

  // ============================================
  // RETRY CONNECTION
  // ============================================
  const retryConnection = async () => {
    if (navigator.onLine) {
      setOffline(false);
      try {
        await refreshWallet();
      } catch (error) {
        console.error('Error reconnecting:', error);
        setOffline(true);
      }
    } else {
      alert('Please check your internet connection');
    }
  };

  // ============================================
  // FORMAT INR
  // ============================================
  const formatINR = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };


  // ============================================
  // HANDLE ADD MONEY
  // ============================================
const handleAddMoney = async () => {
  const amount = parseInt(addAmount);
  
  if (isNaN(amount) || amount < 20) {
    alert("Minimum add is ₹20");
    return;
  }

  if (amount > 10000) {
    alert("Maximum add is ₹10,000");
    return;
  }

  // 🔥 IMPORTANT: Open payment window IMMEDIATELY from user click
  // This must happen BEFORE any await/Firebase call
  const paymentWindow = window.open("", "_blank");

  if (!paymentWindow) {
    alert(
      "Payment window was blocked by your browser.\n\n" +
      "Please allow pop-ups for thearenax.com and try again."
    );
    return;
  }

  // Store reference
  setPaymentWindowRef(paymentWindow);

  try {
    setPaymentLoading(true);
    setPaymentStatus('pending');
    const now = Date.now();
    setPaymentCreatedAt(now);

    // Show loading screen in the payment tab
    paymentWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>ArenaX Payment</title>
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <style>
            body {
              margin: 0;
              background: #0a0a0f;
              color: white;
              font-family: Arial, sans-serif;
              display: flex;
              align-items: center;
              justify-content: center;
              height: 100vh;
              text-align: center;
            }
            .loader {
              width: 45px;
              height: 45px;
              border: 4px solid rgba(139, 92, 246, 0.2);
              border-top-color: #8b5cf6;
              border-radius: 50%;
              animation: spin 1s linear infinite;
              margin: 0 auto 20px;
            }
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
          </style>
        </head>
        <body>
          <div>
            <div class="loader"></div>
            <h2>Preparing Payment...</h2>
            <p>Please wait</p>
          </div>
        </body>
      </html>
    `);
    paymentWindow.document.close();

    const functions = getFunctions();
    const createPayment = httpsCallable(functions, "createPaymentOrder");

    const result = await createPayment({
      amount: amount
    });

    const data = result.data as {
      success: boolean;
      message?: string;
      paymentUrl: string;
      paymentId: string;
    };

    if (!data.success) {
      paymentWindow.close();
      setPaymentWindowRef(null);
      alert(data.message || 'Failed to create payment. Please try again.');
      setPaymentStatus('failed');
      setPaymentLoading(false);
      setPaymentCreatedAt(null);
      return;
    }

    localStorage.setItem("paymentId", data.paymentId);
    localStorage.setItem("paymentAmount", String(amount));
    localStorage.setItem("paymentCreatedAt", String(now));
    setPaymentId(data.paymentId);
    setPaymentAmount(amount);


    // 🔥 IMPORTANT: Navigate the already-open tab to the payment URL
    // Do NOT call window.open() here
    paymentWindow.location.href = data.paymentUrl;

    // Close the modal
    setShowAddMoneyModal(false);
    setAddAmount("");
    setSelectedQuickAmount(null);

  } catch (e: any) {
    console.error('Payment creation error:', e);
    
    // Close the payment window if there's an error
    try {
      paymentWindow.close();
      setPaymentWindowRef(null);
    } catch {}
    
    alert(e.message || 'Failed to create payment. Please try again.');
    setPaymentStatus('failed');
    setPaymentLoading(false);
    setPaymentCreatedAt(null);
    localStorage.removeItem("paymentId");
    localStorage.removeItem("paymentAmount");
    localStorage.removeItem("paymentCreatedAt");
  }
};

  // ============================================
  // HANDLE WITHDRAW REQUEST
  // ============================================
  const handleWithdrawRequest = async () => {
    const amount = parseInt(withdrawAmount);
    
    if (isNaN(amount) || amount < 100) {
      alert('Minimum withdrawal is ₹100');
      return;
    }
    
    if (amount > balance) {
      alert('Insufficient balance');
      return;
    }
    
    if (!withdrawUpi || withdrawUpi.length < 5) {
      alert('Please enter a valid UPI ID');
      return;
    }
    
    setWithdrawLoading(true);
    
    try {
      const functions = getFunctions();
      const requestWithdrawalFn = httpsCallable(functions, 'requestWithdrawal');

      const result = await requestWithdrawalFn({
        amount: amount,
        upiId: withdrawUpi
      });

      const data = result.data as {
        success: boolean;
        message: string;
        newBalance: number;
        transactionId: string;
        status: string;
      };

      if (data.success) {
        setBalance(data.newBalance);
        setShowWithdrawModal(false);
        setWithdrawAmount('');
        setWithdrawUpi('');
        
        await refreshWallet();
        alert(`✅ ${data.message}`);
      }
    } catch (err: any) {
      console.error('Error requesting withdrawal:', err);
      alert(err.message || 'Failed to process withdrawal. Please try again.');
    } finally {
      setWithdrawLoading(false);
    }
  };

  const quickAmounts = [100, 500, 1000, 2000, 5000];

  // ============================================
  // LOADING STATE
  // ============================================
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0A0A0F] to-[#0F0F1A] flex items-center justify-center overflow-hidden">
    <div className="absolute w-60 h-60 bg-purple-500/20 rounded-full blur-3xl animate-pulse" />
    <div className="absolute w-72 h-72 bg-blue-500/10 rounded-full blur-3xl animate-pulse-slow" style={{ top: '20%', left: '10%' }} />
    <div className="absolute w-56 h-56 bg-pink-500/10 rounded-full blur-3xl animate-pulse-slow" style={{ bottom: '20%', right: '10%' }} />
    
    <div className="relative flex flex-col items-center">
      <div className="relative w-28 h-28">
        <div className="absolute inset-0 rounded-full">
          <svg className="w-full h-full animate-spin-slow" viewBox="0 0 100 100">
            <defs>
              <linearGradient id="ringGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#A855F7" />
                <stop offset="50%" stopColor="#3B82F6" />
                <stop offset="100%" stopColor="#EC4899" />
              </linearGradient>
            </defs>
            <circle
              cx="50"
              cy="50"
              r="44"
              fill="none"
              stroke="url(#ringGradient)"
              strokeWidth="3"
              strokeDasharray="200"
              strokeDashoffset="50"
              strokeLinecap="round"
            />
          </svg>
        </div>
        
        <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 via-blue-500/20 to-pink-500/20 blur-xl rounded-full animate-pulse" />
        <div className="absolute -inset-1 bg-gradient-to-r from-purple-500/10 via-blue-500/10 to-pink-500/10 rounded-full blur-md animate-pulse-slow" />
        
        <div className="absolute inset-0 flex items-center justify-center">
          <img
            src={logo}
            alt="ArenaX"
            className="w-20 h-20 object-contain animate-float"
          />
        </div>
      </div>

      <div className="mt-5 text-center">
        <p className="text-transparent bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text font-rajdhani font-bold text-base tracking-widest animate-pulse">
          LOADING ARENA...
        </p>
        <div className="flex items-center justify-center gap-1.5 mt-2">
          <div className="w-1.5 h-1.5 rounded-full bg-purple-500 animate-bounce" style={{ animationDelay: '0s' }} />
          <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '0.2s' }} />
          <div className="w-1.5 h-1.5 rounded-full bg-pink-500 animate-bounce" style={{ animationDelay: '0.4s' }} />
        </div>
      </div>
    </div>
  </div>
    );
  }

  // ============================================
  // OFFLINE STATE
  // ============================================
  if (offline) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a0a0f] to-[#1a1a2e] flex items-center justify-center px-4">
        <motion.div 
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="text-center"
        >
          <div className="w-24 h-24 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <WifiOff className="w-12 h-12 text-yellow-500" />
          </div>
          <h2 className="text-white font-rajdhani font-bold text-2xl mb-2">No Internet Connection</h2>
          <p className="text-gray-400 mb-6">Please check your connection and try again</p>
          <motion.button 
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={retryConnection}
            className="px-8 py-3 bg-gradient-to-r from-purple-500 to-blue-500 rounded-xl text-white font-rajdhani font-bold"
          >
            Retry Connection
          </motion.button>
        </motion.div>
      </div>
    );
  }

  // ============================================
  // MAIN RENDER
  // ============================================
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0f] via-[#12122a] to-[#0a0a0f]">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-1000" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-cyan-500/5 rounded-full blur-3xl animate-pulse-slow" />
      </div>

      {/* Header */}
      <div className="fixed top-0 w-full bg-gradient-to-r from-[#0a0a0f]/95 via-[#12122a]/95 to-[#0a0a0f]/95 backdrop-blur-xl border-b border-purple-500/20 z-40">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <motion.button 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate(-1)} 
              className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500/20 to-blue-500/20 flex items-center justify-center hover:from-purple-500/30 hover:to-blue-500/30 transition-all duration-300"
            >
              <ArrowLeft className="w-5 h-5 text-white" />
            </motion.button>
            
            <div className="text-center">
              <h1 className="text-xl font-rajdhani font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                Gaming Wallet
              </h1>
              <div className="flex items-center justify-center gap-1 mt-0.5">
                <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                <span className="text-green-500 text-[10px] font-rajdhani font-bold">SECURE</span>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-purple-500/20 to-blue-500/20 px-3 py-1 rounded-full border border-purple-500/20">
              <span className="text-purple-400 font-rajdhani font-bold text-xs">@{userName}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="pt-20 pb-24 px-4">
        {/* Balance Card */}
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative mb-6"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-blue-500/20 rounded-2xl blur-xl" />
          <div className="relative bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] rounded-2xl p-6 border border-purple-500/30 overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/10 rounded-full blur-2xl" />
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-blue-500/10 rounded-full blur-2xl" />
            
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Diamond className="w-5 h-5 text-purple-400" />
                  <span className="text-gray-400 text-sm font-rajdhani">TOTAL BALANCE</span>
                </div>
                <Crown className="w-5 h-5 text-yellow-500" />
              </div>
              
              <div className="mb-2">
                <span className="text-5xl font-rajdhani font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                  {formatINR(balance)}
                </span>
              </div>
              
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <TrendingUp className="w-4 h-4 text-green-400" />
                <span>Available for tournaments & withdrawals</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* ============================================ */}
        {/* 🔥 NEW: PAYMENT PENDING BANNER */}
        {/* ============================================ */}
        {paymentStatus === "pending" && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="relative mb-6"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 rounded-2xl blur-xl" />
            <div className="relative bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] rounded-2xl p-5 border border-yellow-500/30 overflow-hidden">
              {/* Decorative pulse ring */}
              <div className="absolute inset-0 border-2 border-yellow-500/10 rounded-2xl animate-pulse" />
              
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 relative z-10">
                <div className="flex items-start gap-3 flex-1">
                  <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center flex-shrink-0 animate-pulse">
                    <Timer className="w-6 h-6 text-yellow-500" />
                  </div>
                  <div>
                    <h3 className="text-yellow-500 font-rajdhani font-bold text-lg">
                      ⏳ Payment Pending
                    </h3>
                    <p className="text-gray-400 text-sm mt-1 max-w-md">
                      We're waiting for confirmation from the payment gateway. 
                      {paymentAmount && (
                        <span className="text-white font-bold ml-1">
                          ₹{paymentAmount}
                        </span>
                      )}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4 text-yellow-400" />
                        <span className="text-yellow-400 font-mono text-sm font-bold">
                          Time remaining: {formatTimeRemaining(timeRemaining)}
                        </span>
                      </div>
                      {isPolling && (
                        <span className="text-xs text-gray-500 flex items-center gap-1">
                          <Loader2 className="w-3 h-3 animate-spin" />
                          Checking status...
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 flex-shrink-0 w-full md:w-auto">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={checkPaymentStatus}
                    disabled={isPolling}
                    className="px-4 py-2 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-xl text-white font-rajdhani font-bold text-sm flex items-center gap-2 shadow-lg shadow-yellow-500/25 disabled:opacity-50"
                  >
                    <RefreshCw className={`w-4 h-4 ${isPolling ? 'animate-spin' : ''}`} />
                    Check Status Now
                  </motion.button>
                  
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={cancelPayment}
                    className="px-4 py-2 bg-red-500/20 border border-red-500/30 rounded-xl text-red-400 font-rajdhani font-bold text-sm flex items-center gap-2 hover:bg-red-500/30 transition-all"
                  >
                    <XCircle className="w-4 h-4" />
                    Cancel
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Action Tabs */}
        <div className="mb-6">
          <div className="bg-white/5 rounded-xl p-1 backdrop-blur-sm border border-white/10 relative">
            <motion.div
              className="absolute top-1 bottom-1 w-1/2 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg shadow-lg"
              animate={{ x: activeTab === 'overview' ? '0%' : '100%' }}
              transition={{ type: "spring", stiffness: 500, damping: 30 }}
            />
            
            <div className="flex gap-1 relative z-10">
              <button
                onClick={() => setActiveTab('overview')}
                className={`flex-1 py-2.5 rounded-lg font-rajdhani font-bold text-sm transition-all duration-300 flex items-center justify-center gap-2 ${
                  activeTab === 'overview' ? 'text-white' : 'text-gray-400 hover:text-white'
                }`}
              >
                <WalletIcon className="w-4 h-4" />
                Quick Actions
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`flex-1 py-2.5 rounded-lg font-rajdhani font-bold text-sm transition-all duration-300 flex items-center justify-center gap-2 ${
                  activeTab === 'history' ? 'text-white' : 'text-gray-400 hover:text-white'
                }`}
              >
                <History className="w-4 h-4" />
                Transaction History
              </button>
            </div>
          </div>
        </div>

        <AnimatePresence mode="wait">
          {activeTab === 'overview' ? (
            <motion.div
              key="overview"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
              className="space-y-4"
            >
              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-4">
                <motion.button
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowAddMoneyModal(true)}
                  disabled={paymentStatus === 'pending'}
                  className="relative group overflow-hidden bg-gradient-to-r from-purple-500 to-blue-500 py-4 rounded-xl text-white font-rajdhani font-bold flex items-center justify-center gap-2 shadow-lg shadow-purple-500/25 disabled:opacity-50"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-500" />
                  <Plus className="w-5 h-5" />
                  Add Money
                </motion.button>
                
                <motion.button
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowWithdrawModal(true)}
                  className="relative group overflow-hidden bg-gradient-to-r from-yellow-500 to-orange-500 py-4 rounded-xl text-white font-rajdhani font-bold flex items-center justify-center gap-2 shadow-lg shadow-yellow-500/25"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-500" />
                  <Send className="w-5 h-5" />
                  Withdraw
                </motion.button>
              </div>

              {/* ============================================ */}
{/* INSTRUCTION SECTION - Add this after Action Buttons */}
{/* ============================================ */}
<motion.div
  initial={{ opacity: 0, y: 20 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ delay: 0.15 }}
  className="bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] rounded-2xl p-5 border border-purple-500/20 overflow-hidden relative"
>
  {/* Decorative gradient orb */}
  <div className="absolute -top-10 -right-10 w-32 h-32 bg-purple-500/10 rounded-full blur-2xl" />
  <div className="absolute -bottom-10 -left-10 w-24 h-24 bg-blue-500/10 rounded-full blur-2xl" />
  
  <div className="relative z-10">
    {/* Header */}
    <div className="flex items-center gap-2 mb-4">
      <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center">
        <Info className="w-4 h-4 text-white" />
      </div>
      <h3 className="text-white font-rajdhani font-bold text-lg">Payment Instructions</h3>
      <span className="ml-auto text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full flex items-center gap-1">
        <Zap className="w-3 h-3" />
        100% Secure
      </span>
    </div>

    {/* Steps */}
    <div className="space-y-3">
      {/* Step 1 */}
      <div className="flex items-start gap-3 p-2 rounded-xl bg-white/5 hover:bg-white/10 transition-all duration-300">
        <div className="w-7 h-7 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5">
          <span className="text-white font-rajdhani font-bold text-xs">1</span>
        </div>
        <div className="flex-1">
          <p className="text-white font-rajdhani font-bold text-sm">
            Enter Amount
          </p>
          <p className="text-gray-400 text-xs">
            Minimum ₹20 • Maximum ₹10,000
          </p>
        </div>
        <Diamond className="w-4 h-4 text-purple-400 flex-shrink-0" />
      </div>

      {/* Step 2 */}
      <div className="flex items-start gap-3 p-2 rounded-xl bg-white/5 hover:bg-white/10 transition-all duration-300">
        <div className="w-7 h-7 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5">
          <span className="text-white font-rajdhani font-bold text-xs">2</span>
        </div>
        <div className="flex-1">
          <p className="text-white font-rajdhani font-bold text-sm">
            Click "Proceed to Payment"
          </p>
          <p className="text-gray-400 text-xs">
            You'll be redirected to the secure payment gateway
          </p>
        </div>
        <ArrowUpRight className="w-4 h-4 text-blue-400 flex-shrink-0" />
      </div>

      {/* Step 3 */}
      <div className="flex items-start gap-3 p-2 rounded-xl bg-white/5 hover:bg-white/10 transition-all duration-300">
        <div className="w-7 h-7 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5">
          <span className="text-white font-rajdhani font-bold text-xs">3</span>
        </div>
        <div className="flex-1">
          <p className="text-white font-rajdhani font-bold text-sm">
            Complete Payment
          </p>
          <p className="text-gray-400 text-xs">
            Use any UPI app (PhonePe, GPay, Paytm, etc.)
          </p>
        </div>
        <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
      </div>

      {/* Step 4 */}
      <div className="flex items-start gap-3 p-2 rounded-xl bg-white/5 hover:bg-white/10 transition-all duration-300">
        <div className="w-7 h-7 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5">
          <span className="text-white font-rajdhani font-bold text-xs">4</span>
        </div>
        <div className="flex-1">
          <p className="text-white font-rajdhani font-bold text-sm">
            Auto-Credit
          </p>
          <p className="text-gray-400 text-xs">
            Wallet will be credited automatically after successful payment
          </p>
        </div>
        <Sparkles className="w-4 h-4 text-yellow-400 flex-shrink-0" />
      </div>
    </div>

    {/* Important Notes */}
    <div className="mt-4 p-3 bg-yellow-500/10 rounded-xl border border-yellow-500/20">
      <div className="flex items-start gap-2">
        <AlertCircle className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-yellow-400 text-xs font-rajdhani font-bold">
            ⚠️ Important Notes
          </p>
          <ul className="text-gray-300 text-xs space-y-1 mt-1">
            <li className="flex items-start gap-1">
              <span className="text-yellow-400">•</span>
              <span>Do NOT refresh or close the page during payment</span>
            </li>
            <li className="flex items-start gap-1">
              <span className="text-yellow-400">•</span>
              <span>If payment is successful and balance not updated within 2 minutes, contact support with screenshot</span>
            </li>
            <li className="flex items-start gap-1">
              <span className="text-yellow-400">•</span>
              <span className="text-blue-400 font-medium">
                💡 iOS/Chrome users: If payment page says "This page is protected", use Safari browser
              </span>
            </li>
          </ul>
        </div>
      </div>
    </div>

    {/* Trust Badges */}
    <div className="flex items-center justify-center gap-4 mt-4 pt-3 border-t border-white/10">
      <div className="flex items-center gap-1.5">
        <Shield className="w-4 h-4 text-green-400" />
        <span className="text-gray-400 text-xs">SSL Secure</span>
      </div>
      <div className="w-px h-4 bg-white/10" />
      <div className="flex items-center gap-1.5">
        <Clock className="w-4 h-4 text-blue-400" />
        <span className="text-gray-400 text-xs">Instant Credit</span>
      </div>
      <div className="w-px h-4 bg-white/10" />
      <div className="flex items-center gap-1.5">
        <Award className="w-4 h-4 text-yellow-400" />
        <span className="text-gray-400 text-xs">Trusted</span>
      </div>
    </div>
  </div>
</motion.div>
            </motion.div>
          ) : (
            <motion.div
              key="history"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-3"
            >
              {transactions.length === 0 ? (
                <motion.div 
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] rounded-2xl p-12 text-center border border-purple-500/20"
                >
                  <WalletIcon className="w-16 h-16 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400 font-rajdhani text-lg">No transactions yet</p>
                  <p className="text-gray-500 text-sm mt-1">Add money to start your gaming journey!</p>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowAddMoneyModal(true)}
                    className="mt-4 px-6 py-2 bg-gradient-to-r from-purple-500 to-blue-500 rounded-xl text-white font-rajdhani font-bold text-sm"
                  >
                    Add Money Now
                  </motion.button>
                </motion.div>
              ) : (
                <AnimatePresence>
                  {transactions.map((tx, index) => (
                    <motion.div
                      key={tx.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      whileHover={{ scale: 1.02, x: 5 }}
                      className="bg-gradient-to-r from-[#1a1a2e] to-[#0a0a0f] rounded-xl p-4 border border-white/10 hover:border-purple-500/30 transition-all duration-300"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            tx.type === 'credit' ? 'bg-green-500/20' : 'bg-red-500/20'
                          }`}>
                            {tx.type === 'credit' ? (
                              <Plus className="w-5 h-5 text-green-400" />
                            ) : (
                              <ArrowUpRight className="w-5 h-5 text-red-400" />
                            )}
                          </div>
                          <div>
                            <p className="text-white font-rajdhani font-bold text-sm">{tx.reason}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Clock className="w-3 h-3 text-gray-500" />
                              <p className="text-gray-500 text-xs">
                                {new Date(tx.time).toLocaleDateString()} • {new Date(tx.time).toLocaleTimeString()}
                              </p>
                              {tx.status === 'pending' && (
                                <span className="text-yellow-500 text-xs bg-yellow-500/20 px-2 py-0.5 rounded-full">Pending</span>
                              )}
                              {tx.status === 'success' && (
                                <span className="text-green-500 text-xs bg-green-500/20 px-2 py-0.5 rounded-full">Completed</span>
                              )}
                              {tx.status === 'rejected' && tx.rejectReason && (
  <div className="mt-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20">
    <p className="text-red-400 text-xs font-bold">
      Rejection Reason
    </p>
    <p className="text-gray-400 text-xs mt-1">
      {tx.rejectReason}
    </p>
  </div>
)}
                            </div>
                          </div>
                        </div>
                        <div className={`font-rajdhani font-bold text-lg ${
                          tx.type === 'credit' ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {tx.type === 'credit' ? '+' : '-'}{formatINR(tx.amount)}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

{/* Add Money Modal */}
<AnimatePresence>
  {showAddMoneyModal && (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-50 p-4"
      onClick={() => {
        if (paymentLoading || paymentStatus === 'pending') return;
        setShowAddMoneyModal(false);
      }}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] rounded-2xl max-w-md w-full p-6 border border-purple-500/30 shadow-2xl"
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-white font-rajdhani font-bold text-2xl flex items-center gap-2">
            <Diamond className="w-6 h-6 text-purple-400" />
            Add Money
          </h2>
          <motion.button
            whileHover={{ scale: 1.1, rotate: 90 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => {
              if (paymentLoading || paymentStatus === 'pending') return;
              setShowAddMoneyModal(false);
              setPaymentStatus('idle');
            }}
            className="text-gray-400 hover:text-white text-2xl"
          >
            ✕
          </motion.button>
        </div>
        
        <div className="mb-6">
          <label className="block text-gray-300 text-sm mb-2 font-rajdhani">Enter Amount (₹)</label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl text-purple-400">₹</span>
            <input
              type="number"
              value={addAmount}
              onChange={(e) => {
                setAddAmount(e.target.value);
                setSelectedQuickAmount(null);
              }}
              placeholder="100"
              disabled={paymentLoading || paymentStatus === 'pending'}
              className={`w-full bg-black/30 border rounded-xl px-12 py-4 text-white text-2xl font-rajdhani focus:outline-none transition disabled:opacity-50 ${
                addAmount && parseInt(addAmount) > 0 && parseInt(addAmount) < 20
                  ? 'border-red-500/50 focus:border-red-500'
                  : 'border-purple-500/30 focus:border-purple-500'
              }`}
            />
          </div>
          {/* 🔥 NEW: Minimum amount warning */}
          {addAmount && parseInt(addAmount) > 0 && parseInt(addAmount) < 20 && (
            <motion.p
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-red-400 text-sm mt-2 font-rajdhani flex items-center gap-1.5"
            >
              <AlertCircle className="w-4 h-4" />
              Minimum add amount is ₹20
            </motion.p>
          )}
        </div>
        
        <div className="mb-6">
          <label className="block text-gray-300 text-sm mb-2 font-rajdhani">Quick Select</label>
          <div className="grid grid-cols-5 gap-2">
            {quickAmounts.map((amt) => (
              <motion.button
                key={amt}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  if (paymentLoading || paymentStatus === 'pending') return;
                  setAddAmount(String(amt));
                  setSelectedQuickAmount(amt);
                }}
                disabled={paymentLoading || paymentStatus === 'pending'}
                className={`py-2 rounded-lg font-rajdhani font-bold transition-all ${
                  selectedQuickAmount === amt
                    ? 'bg-gradient-to-r from-purple-500 to-blue-500 text-white'
                    : 'bg-white/10 text-gray-300 hover:bg-white/20'
                } disabled:opacity-50`}
              >
                ₹{amt}
              </motion.button>
            ))}
          </div>
        </div>
        
        <div className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 rounded-xl p-3 mb-6">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-gray-400">You will add:</span>
            <span className="text-purple-400 font-bold">{formatINR(parseInt(addAmount) || 0)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-400">Credits will be added instantly to:</span>
            <span className="text-green-400 font-bold">{formatINR(parseInt(addAmount) || 0)}</span>
          </div>
        </div>

        {paymentStatus === "failed" && (
          <div className="text-red-400 text-sm text-center mb-4">
            ❌ Payment Failed
          </div>
        )}

        {paymentStatus === "expired" && (
          <div className="text-orange-400 text-sm text-center mb-4">
            ⏰ Payment Expired
          </div>
        )}

        {paymentStatus === "cancelled" && (
          <div className="text-gray-400 text-sm text-center mb-4">
            ❌ Payment Cancelled
          </div>
        )}
        
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleAddMoney}
          disabled={paymentLoading || !addAmount || parseInt(addAmount) < 20 || paymentStatus === 'pending'}
          className={`w-full py-4 rounded-xl text-white font-rajdhani font-bold text-lg shadow-lg relative overflow-hidden group ${
            addAmount && parseInt(addAmount) > 0 && parseInt(addAmount) < 20
              ? 'bg-gray-600 cursor-not-allowed'
              : 'bg-gradient-to-r from-purple-500 to-blue-500 shadow-purple-500/25'
          } disabled:opacity-50`}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-500" />
          {paymentLoading || paymentStatus === 'pending' ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              Creating Payment...
            </span>
          ) : addAmount && parseInt(addAmount) > 0 && parseInt(addAmount) < 20 ? (
            'Minimum ₹20 required'
          ) : (
            'Proceed to Payment'
          )}
        </motion.button>
      </motion.div>
    </motion.div>
  )}
</AnimatePresence>

      {/* Withdraw Modal */}
      <AnimatePresence>
        {showWithdrawModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-50 p-4"
            onClick={() => setShowWithdrawModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] rounded-2xl max-w-md w-full p-6 border border-yellow-500/30 shadow-2xl"
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-white font-rajdhani font-bold text-2xl flex items-center gap-2">
                  <Send className="w-6 h-6 text-yellow-400" />
                  Withdraw Funds
                </h2>
                <motion.button
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setShowWithdrawModal(false)}
                  className="text-gray-400 hover:text-white text-2xl"
                >
                  ✕
                </motion.button>
              </div>

           
              <div className="mb-4">
                <label className="block text-gray-300 text-sm mb-2 font-rajdhani">Amount to Withdraw (₹)</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl text-yellow-400">₹</span>
                  <input
                    type="number"
                    value={withdrawAmount}
                    onChange={(e) => setWithdrawAmount(e.target.value)}
                    placeholder={`Min 100`}
                    className="w-full bg-black/30 border border-yellow-500/30 rounded-xl px-12 py-4 text-white text-2xl font-rajdhani focus:outline-none focus:border-yellow-500 transition"
                  />
                </div>
                <p className="text-gray-500 text-xs mt-1">Available balance: {formatINR(balance)}</p>
              </div>
              
              <div className="mb-6">
                <label className="block text-gray-300 text-sm mb-2 font-rajdhani">UPI ID</label>
                <input
                  type="text"
                  value={withdrawUpi}
                  onChange={(e) => setWithdrawUpi(e.target.value)}
                  placeholder="example@okhdfcbank"
                  className="w-full bg-black/30 border border-yellow-500/30 rounded-xl px-4 py-3 text-white font-rajdhani focus:outline-none focus:border-yellow-500 transition"
                />
                <p className="text-gray-500 text-xs mt-1">We'll send money to this UPI ID</p>
              </div>
              
              <div className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 rounded-xl p-3 mb-6">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-400">You will receive:</span>
                  <span className="text-yellow-400 font-bold">{formatINR(parseInt(withdrawAmount) || 0)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Processing time:</span>
                  <span className="text-orange-400 font-bold">Within 24 hours</span>
                </div>
              </div>
              
              <div className="flex items-center gap-2 mb-4 p-3 bg-yellow-500/10 rounded-xl border border-yellow-500/20">
                <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                <p className="text-gray-300 text-xs">Withdrawals are processed after tournament completion. Minimum withdrawal ₹100.</p>
              </div>
              
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleWithdrawRequest}
                disabled={withdrawLoading}
                className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 py-4 rounded-xl text-white font-rajdhani font-bold text-lg shadow-lg shadow-yellow-500/25 disabled:opacity-50 relative overflow-hidden group"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-500" />
                {withdrawLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Processing...
                  </span>
                ) : (
                  'Request Withdrawal'
                )}
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@300;400;500;600;700&display=swap');
        
        .font-rajdhani {
          font-family: 'Rajdhani', sans-serif;
        }
        
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.3; transform: translate(-50%, -50%) scale(1); }
          50% { opacity: 0.6; transform: translate(-50%, -50%) scale(1.1); }
        }
        
        .animate-pulse-slow {
          animation: pulse-slow 4s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default Wallet;